import type { ProblemDetails } from './types';

const STATUS_TEXT: Record<number, string> = {
  400: 'Bad request',
  401: 'Unauthorized',
  403: 'Forbidden',
  404: 'Not found',
  429: 'Too many requests',
  502: 'The identity provider could not be reached',
  504: 'The identity provider timed out',
};

/** A non-2xx response from AUTH. */
export class AuthApiError extends Error {
  readonly status: number;
  readonly body?: ProblemDetails;
  /** Seconds to wait before retrying (`Retry-After`, 429 only). */
  readonly retryAfter?: number;

  constructor(message: string, status: number, opts: { body?: ProblemDetails; retryAfter?: number } = {}) {
    super(message);
    this.name = 'AuthApiError';
    this.status = status;
    this.body = opts.body;
    this.retryAfter = opts.retryAfter;
  }

  get isUnauthorized(): boolean {
    return this.status === 401;
  }
  get isForbidden(): boolean {
    return this.status === 403;
  }
  get isNotFound(): boolean {
    return this.status === 404;
  }
}

/** Message of a ProblemDetails body: validation errors joined, else `detail`, `title` or `error`. */
export function problemMessage(body: ProblemDetails | undefined, status: number, text = ''): string {
  if (body?.errors) {
    const lines = Object.values(body.errors).flat().filter(Boolean);
    if (lines.length) return lines.join(' ');
  }
  return (
    body?.detail ||
    body?.error ||
    (body?.title && body.title !== STATUS_TEXT[status] ? body.title : '') ||
    (text && text.length < 300 && !text.startsWith('<') ? text : '') ||
    STATUS_TEXT[status] ||
    `HTTP ${status}`
  );
}

/** Turn a failed response into an `AuthApiError` (ProblemDetails, `{error}`, plain text and empty bodies). */
export async function errorFromResponse(response: Response): Promise<AuthApiError> {
  let body: ProblemDetails | undefined;
  let text = '';
  try {
    text = await response.text();
    body = text ? (JSON.parse(text) as ProblemDetails) : undefined;
  } catch {
    body = undefined;
  }
  return new AuthApiError(problemMessage(body, response.status, text), response.status, {
    body,
    retryAfter: retryAfterSeconds(response),
  });
}

function retryAfterSeconds(response: Response): number | undefined {
  const header = response.headers?.get?.('Retry-After');
  if (!header) return undefined;
  const seconds = Number(header);
  if (isFinite(seconds)) return Math.max(0, seconds);
  const at = Date.parse(header);
  return isNaN(at) ? undefined : Math.max(0, Math.round((at - Date.now()) / 1000));
}
